import React, { useState, useEffect } from 'react';

const TOTAL_BTC = 21000000;
const SATS_PER_BTC = 100000000;
const TOTAL_SATS = TOTAL_BTC * SATS_PER_BTC;
const BLOCKS_PER_HALVING = 210000;
const INITIAL_REWARD = 50;

// Bitcoin ownership by country (2025 estimates)
const ownershipData = [
  { country: 'United Arab Emirates', percentage: 30.4, owners: 3040000 },
  { country: 'Vietnam', percentage: 21.2, owners: 21000000 },
  { country: 'United States', percentage: 15.6, owners: 52000000 },
  { country: 'Brazil', percentage: 17.5, owners: 36000000 },
  { country: 'Singapore', percentage: 24.4, owners: 1800000 },
  { country: 'United Kingdom', percentage: 18.0, owners: 12000000 },
  { country: 'Turkey', percentage: 27.1, owners: 22000000 },
  { country: 'Argentina', percentage: 18.9, owners: 8500000 },
  { country: 'India', percentage: 7.0, owners: 93000000 },
  { country: 'China', percentage: 4.2, owners: 59000000 },
  { country: 'Nigeria', percentage: 13.0, owners: 28000000 },
  { country: 'Thailand', percentage: 12.0, owners: 8400000 },
  { country: 'Philippines', percentage: 13.0, owners: 14000000 },
  { country: 'Indonesia', percentage: 41.0, owners: 41000000 },
  { country: 'Saudi Arabia', percentage: 11.4, owners: 4000000 },
  { country: 'South Africa', percentage: 10.0, owners: 6000000 },
  { country: 'Russia', percentage: 8.0, owners: 11000000 },
  { country: 'Australia', percentage: 9.0, owners: 2300000 },
  { country: 'Canada', percentage: 8.0, owners: 3200000 },
  { country: 'Germany', percentage: 6.0, owners: 5000000 },
];

function BitcoinCountdown() {
  const [blockHeight, setBlockHeight] = useState(null);
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const [error, setError] = useState(null);
  const [showWallet, setShowWallet] = useState(false);
  const walletAddress = 'bc1q2gc4qxfja8kp9j77jj2d2gcyc7tyf6ltu0ks4r';

  useEffect(() => {
    async function fetchData() {
      try {
        const heightResponse = await fetch('/api/latestblock');
        if (!heightResponse.ok) throw new Error('Failed to fetch block height');
        const heightData = await heightResponse.json();
        setBlockHeight(heightData.height);

        const priceResponse = await fetch('/api/ticker');
        if (!priceResponse.ok) throw new Error('Failed to fetch price');
        const priceData = await priceResponse.json();
        setBitcoinPrice(priceData.USD.last);
      } catch (error) {
        setError(error.message);
        console.error('Error fetching data:', error);
      }
    }
    fetchData();
  }, []);

  const calculateMinedBtc = (height) => {
    let totalBtc = 0;
    let remainingBlocks = height;
    let reward = INITIAL_REWARD;

    while (remainingBlocks > 0) {
      const blocksInPeriod = Math.min(remainingBlocks, BLOCKS_PER_HALVING);
      totalBtc += blocksInPeriod * reward;
      reward /= 2;
      remainingBlocks -= blocksInPeriod;
    }
    return totalBtc;
  };

  const handleDonateClick = () => setShowWallet(true);

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(walletAddress);
    alert('Wallet address copied!');
  };

  if (error) return <div className="text-center text-danger">Error: {error}</div>;
  if (!blockHeight || !bitcoinPrice) return <div className="text-center text-warning">Loading...</div>;

  const minedBtc = calculateMinedBtc(blockHeight);
  const btcLeft = TOTAL_BTC - minedBtc;
  const btcLeftFormatted = btcLeft.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const minedSats = Math.round(minedBtc * SATS_PER_BTC);
  const satsLeft = TOTAL_SATS - minedSats;
  const satPrice = bitcoinPrice / SATS_PER_BTC;

  const satoshiValues = [
    { sats: 1, usd: satPrice * 1 },
    { sats: 10, usd: satPrice * 10 },
    { sats: 100, usd: satPrice * 100 },
    { sats: 1000, usd: satPrice * 1000 },
    { sats: 10000, usd: satPrice * 10000 },
  ];

  return (
    <div className="container mx-auto" style={{ maxWidth: '900px' }}>
      <div className="card bg-dark border-warning mb-4">
        <div className="card-body text-center p-4">
          <div className="mb-4">
            <h2 className="text-warning">Bitcoins Left To Mine</h2>
            <div className="display-4 text-white">{btcLeftFormatted}</div>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              The last Bitcoin is expected to be mined around 2140. Bitcoin (BTC) is a digital currency with a total supply of 21 million coins.
            </p>
          </div>
          <div className="mb-4">
            <h2 className="text-warning">Sats Left</h2>
            <div className="display-4 text-white">{satsLeft.toLocaleString()}</div>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              The smallest denomination of Bitcoin is called a Satoshi (Sat). It is similar to a penny in USD, with 1 Satoshi equaling 0.00000001 BTC.
              At the current Bitcoin price of ${bitcoinPrice.toLocaleString()} USD, 1 Sat is worth ${satPrice.toFixed(8)} USD.
            </p>
          </div>
          <div className="mb-4">
            <h2 className="text-warning">Satoshi to USD Comparison</h2>
            <div className="table-responsive">
              <table className="table table-bordered text-white">
                <thead className="bg-dark">
                  <tr>
                    <th>Satoshi Amount</th>
                    <th>USD Value</th>
                  </tr>
                </thead>
                <tbody>
                  {satoshiValues.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: '#444' }}>
                      <td>{item.sats.toLocaleString()} Sats</td>
                      <td>${item.usd.toFixed(8)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              Live prices sourced from blockchain.info via API.
            </p>
          </div>
          <div className="mb-4">
            <h2 className="text-warning">Bitcoin Ownership by Country (2025 Estimates)</h2>
            <div className="table-responsive">
              <table className="table table-bordered text-white">
                <thead className="bg-dark">
                  <tr>
                    <th>Country</th>
                    <th>Ownership (%)</th>
                    <th>Number of Owners</th>
                  </tr>
                </thead>
                <tbody>
                  {ownershipData.map((data, index) => (
                    <tr key={index} style={{ backgroundColor: '#444' }}>
                      <td>{data.country}</td>
                      <td>{data.percentage}%</td>
                      <td>{data.owners.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              Data sourced from Triple-A, Statista, and World Population Review (2023-2025 estimates).
            </p>
          </div>
          <div>
            {!showWallet ? (
              <button className="btn btn-warning" onClick={handleDonateClick}>Donate</button>
            ) : (
              <div className="d-flex justify-content-center align-items-center">
                <span className="text-white me-2">{walletAddress}</span>
                <button className="btn btn-outline-warning" onClick={handleCopyWallet}>
                  Copy
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inline CSS for simplicity */}
      <style jsx>{`
        .table tbody tr {
          background-color: #444; /* Faint grey for rows */
        }
        .table thead {
          background-color: #222; /* Slightly darker header */
        }
      `}</style>
    </div>
  );
}

export default BitcoinCountdown;